import { defineComponent as _defineComponent } from 'vue'
import { ref, computed } from 'vue'
import { useQueryLoading } from '@vue/apollo-composable'
import {
  StakeholderCategoryType,
  useCreateImpactMutation,
  useDeleteImpactMutation,
  useGetImpactGoalQuery,
  useUpdateImpactMutation,
} from '~/types/types'
import {
  GHG_ESTIMATOR_CONCEPT_ID,
  IMPACT_STRATEGY_CONCEPT_ID,
  IMPACT_CARRY_CONCEPT_ID,
  impactIconForConceptId,
} from '~/constants/concepts'
import { canEditImpact, useRealUserIsIxAdmin } from '~/functions/impact-locking'

interface Props {
  ventureId: string
  value?: any
}


export default /*#__PURE__*/_defineComponent({
  __name: 'DefineImpacts',
  props: {
    ventureId: null,
    value: null
  },
  setup(__props: any) {

const props = __props as {
  ventureId: string
  value?: any
};



const realUserIsIxAdmin = useRealUserIsIxAdmin()
const impactIsEditable = (impact: { isEditable?: boolean | null }) =>
  canEditImpact(impact, realUserIsIxAdmin.value)

const { result: impactGoalResult, refetch } = useGetImpactGoalQuery({
  ventureId: props.ventureId,
})
const impactGoal = computed(() => impactGoalResult.value?.getImpactGoal)
const tocConceptId = IMPACT_STRATEGY_CONCEPT_ID
const impacts = computed(() =>
  (impactGoal.value?.impacts || []).filter(
    (impact: any) => impact.conceptId !== tocConceptId,
  ),
)

const mitigationConceptId = GHG_ESTIMATOR_CONCEPT_ID
const hasMitigationImpact = computed(() =>
  impacts.value.some((impact: any) => impact.conceptId === mitigationConceptId),
)
const impactCarryConceptId = IMPACT_CARRY_CONCEPT_ID
const hasImpactCarryImpact = computed(() =>
  impacts.value.some((impact: any) => impact.conceptId === impactCarryConceptId),
)
const { mutate: deleteImpact, onDone: onDeleteImpactDone } =
  useDeleteImpactMutation()
const { mutate: createImpact, onDone: onCreateImpactDone } =
  useCreateImpactMutation()
const { mutate: updateImpact } = useUpdateImpactMutation()

onDeleteImpactDone(() => {
  refetch()
})
const templateLoading = ref(false)

onCreateImpactDone(() => {
  templateLoading.value = false
  refetch()
})

const handleImpactInput = async (impactInput: any, updateInput?: any) => {
  templateLoading.value = true
  const impact = await createImpact({
    input: impactInput,
  })
  const newImpactId = impact?.data?.createImpact?.id || null
  if (updateInput && newImpactId) {
    updateImpact({ id: newImpactId, input: updateInput })
  }
}

const handleCreateMitigation = () => {
  if (!impactGoal.value) return
  handleImpactInput(
    {
      // GHG estimator (was Reduced CO2 emissions)
      impactGoalId: impactGoal.value.id,
      conceptId: mitigationConceptId,
      hasFrozenOutcome: true,
      stakeholderCategory: StakeholderCategoryType.PeopleAndNaturalEnvironment,
    },
    {
      geographies: [280],
      irisCategories: [4],
    },
  )
}

const handleCreateImpactCarry = () => {
  if (!impactGoal.value) return
  handleImpactInput({
    impactGoalId: impactGoal.value.id,
    conceptId: impactCarryConceptId,
    stakeholderCategory: StakeholderCategoryType.PeopleAndNaturalEnvironment,
  })
}

const handleCreateCustom = () => {
  if (!impactGoal.value) return
  templateLoading.value = true
  createImpact({
    input: {
      impactGoalId: impactGoal.value.id,
    },
  })
}

function nuxtLink(id: string) {
  const impact = impacts?.value?.find((i: any) => i.id == id)
  const base = `/${props.ventureId}/impact/edit/impacts/${id}`
  const child =
    impact?.conceptId === IMPACT_STRATEGY_CONCEPT_ID
      ? 'impactStrategy'
      : impact?.conceptId === IMPACT_CARRY_CONCEPT_ID
      ? 'impactCarry'
      : 'define'
  return { path: `${base}/${child}` }
}

return { __sfc: true,props, realUserIsIxAdmin, impactIsEditable, impactGoalResult, refetch, impactGoal, tocConceptId, impacts, mitigationConceptId, hasMitigationImpact, impactCarryConceptId, hasImpactCarryImpact, deleteImpact, onDeleteImpactDone, createImpact, onCreateImpactDone, updateImpact, templateLoading, handleImpactInput, handleCreateMitigation, handleCreateImpactCarry, handleCreateCustom, nuxtLink, impactIconForConceptId }
}

})